import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart } from "lucide-react";
import { Switch } from "../ui/switch";
import {
  loadTelemetryPreference,
  saveTelemetryPreference,
  rotateSessionId,
} from "@/lib/telemetry";
import { enableAnalytics, disableAnalytics } from "@/lib/analytics";

// A soft "about" overlay with a single opt-in toggle for anonymous telemetry.
// Modeled after the Grounding / Breathing overlays so the design language
// stays consistent.
export const AboutOverlay = ({ open, onClose }) => {
  const [telemetryOn, setTelemetryOn] = useState(false);

  useEffect(() => {
    if (open) setTelemetryOn(loadTelemetryPreference());
  }, [open]);

  const handleToggle = (checked) => {
    setTelemetryOn(checked);
    saveTelemetryPreference(checked);
    if (checked) {
      // Lazily load PostHog for anonymous page-view analytics — session
      // recording and autocapture are hard-disabled in analytics.js.
      enableAnalytics();
    } else {
      // Immediately stop capture and reset identifiers.
      disableAnalytics();
      // Rotate the sound-blend session id — so opting back on starts fresh
      // and never links to prior data.
      rotateSessionId();
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="about-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.0, ease: "easeInOut" }}
        className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
        data-testid="about-overlay"
      >
        <div className="fixed inset-0 bg-black/45 backdrop-blur-2xl pointer-events-none" aria-hidden="true" />

        <button
          onClick={onClose}
          className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
          style={{
            top: "calc(env(safe-area-inset-top) + 1rem)",
            right: "calc(env(safe-area-inset-right) + 1rem)",
          }}
          data-testid="about-close-btn"
          aria-label="Close about"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="relative min-h-full flex items-center justify-center px-6 py-24 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut", delay: 0.1 }}
          className="relative w-full max-w-lg rounded-3xl backdrop-blur-xl bg-white/5 border border-white/10 px-6 py-8 sm:px-10 sm:py-12"
        >
          <div className="flex items-center gap-3 mb-6 text-white/70">
            <Heart size={16} strokeWidth={1.5} />
            <span className="text-[11px] tracking-[0.25em] uppercase">
              about stillwater
            </span>
          </div>

          <p className="font-heading italic text-white/90 text-2xl leading-relaxed mb-8">
            A small quiet place to slow down when the mind is racing. Colors,
            sounds, and words — nothing to do, nothing to solve.
          </p>

          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p
                  className="font-heading italic text-white/85 text-lg mb-1"
                  data-testid="telemetry-label"
                >
                  help improve stillwater
                </p>
                <p className="text-white/50 text-xs leading-relaxed">
                  Anonymous. Off by default. If on, we log which calming
                  sounds you blend, plus an anonymous page-view — never your
                  quotes, breath sessions, IP address, or anything that could
                  identify you.
                </p>
              </div>
              <Switch
                checked={telemetryOn}
                onCheckedChange={handleToggle}
                data-testid="telemetry-toggle"
                aria-label="Help improve stillwater — anonymous sound-blend data"
              />
            </div>

            <details
              className="mt-4 text-white/45 text-xs group"
              data-testid="telemetry-details"
            >
              <summary className="cursor-pointer hover:text-white/70 transition-colors duration-500 select-none">
                what exactly is collected?
              </summary>
              <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-white/25 leading-relaxed">
                <li>Which two ambient sounds are playing together.</li>
                <li>Which curated blend, if any, was tapped.</li>
                <li>
                  Anonymous page views (no session replay, no click tracking,
                  no IP or location — those are hard-disabled in the code).
                </li>
                <li>
                  A rotating anonymous session id (a random UUID, no account,
                  no IP linking). Turning this off wipes the current id and
                  stops all analytics immediately.
                </li>
              </ul>
              <p className="mt-3 leading-relaxed">
                We do not store: your quotes, breathing or grounding sessions,
                haptic preferences, light-style choices, volume, or anything
                you might type.
              </p>
            </details>
          </div>
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AboutOverlay;
